/**
 * WorkshopEquipmentFormDialog Component (#2323)
 *
 * Dialog for creating/editing non-lift workshop equipment.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Autocomplete,
  Chip,
  DialogContent,
  TextField,
  Stack,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import {
  useCreateWorkshopEquipment,
  useUpdateWorkshopEquipment,
  type WorkshopEquipment,
} from '@/hooks';
import { useNotify } from '@/hooks/useNotify';
import { ApiError } from '@/lib/api';

interface WorkshopEquipmentFormDialogProps {
  open: boolean;
  equipment?: WorkshopEquipment | null;
  onClose: () => void;
}

interface FormData {
  name: string;
  identifier: string;
  location: string;
  description: string;
  senderEmails: string[];
}

const initialFormData: FormData = {
  name: '',
  identifier: '',
  location: '',
  description: '',
  senderEmails: [],
};

const SENDER_EMAILS_MAX = 20;

const SENDER_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidSenderEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= 320 && SENDER_EMAIL_PATTERN.test(trimmed);
}

export function WorkshopEquipmentFormDialog({
  open,
  equipment,
  onClose,
}: WorkshopEquipmentFormDialogProps) {
  const { t } = useTranslation('equipment');
  const { t: ts } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isEditing = !!equipment;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<'name' | 'identifier' | 'senderEmails', string>>>({});

  const createMutation = useCreateWorkshopEquipment();
  const updateMutation = useUpdateWorkshopEquipment();

  useEffect(() => {
    if (equipment) {
      setFormData({
        name: equipment.name,
        identifier: equipment.identifier,
        location: equipment.location ?? '',
        description: equipment.description ?? '',
        senderEmails: equipment.senderEmails ?? [],
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setIsDirty(false);
  }, [equipment, open]);

  const validate = (): boolean => {
    const next: Partial<Record<'name' | 'identifier' | 'senderEmails', string>> = {};
    if (!formData.name.trim()) {
      next.name = t('equipmentForm.nameRequired');
    } else if (formData.name.trim().length > 100) {
      next.name = t('equipmentForm.nameMaxLength');
    }
    if (!formData.identifier.trim()) {
      next.identifier = t('equipmentForm.identifierRequired');
    } else if (formData.identifier.trim().length > 50) {
      next.identifier = t('equipmentForm.identifierMaxLength');
    }
    if (formData.senderEmails.some((email) => !isValidSenderEmail(email))) {
      next.senderEmails = ts('inboundEmail.senderEmailInvalid');
    } else if (formData.senderEmails.length > SENDER_EMAILS_MAX) {
      next.senderEmails = ts('inboundEmail.senderEmailsMax');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      name: formData.name.trim(),
      identifier: formData.identifier.trim(),
      location: formData.location.trim() || null,
      description: formData.description.trim() || null,
      senderEmails: formData.senderEmails,
    };

    try {
      if (isEditing && equipment) {
        await updateMutation.mutateAsync({ id: equipment.id, data: payload });
        notify.success(
          t('equipmentForm.updated'),
          t('equipmentForm.updatedDesc', { name: payload.name }),
        );
      } else {
        await createMutation.mutateAsync(payload);
        notify.success(
          t('equipmentForm.created'),
          t('equipmentForm.createdDesc', { name: payload.name }),
        );
      }
      onClose();
    } catch (error) {
      // A duplicate identifier is a user mistake, not a failure — say which.
      if (error instanceof ApiError && error.status === 409) {
        setErrors({ identifier: t('equipmentForm.identifierTaken') });
        return;
      }
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('equipmentForm.saveFailed') }));
    }
  };

  const handleChange = (field: 'name' | 'identifier' | 'location' | 'description', value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setIsDirty(true);
  };

  const handleSenderEmailsChange = (emails: string[]) => {
    const normalized = emails
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email.length > 0);
    if (normalized.length > SENDER_EMAILS_MAX) {
      setErrors((prev) => ({ ...prev, senderEmails: ts('inboundEmail.senderEmailsMax') }));
      return;
    }
    const invalid = normalized.some((email) => !isValidSenderEmail(email));
    setFormData((prev) => ({ ...prev, senderEmails: normalized }));
    setErrors((prev) => ({
      ...prev,
      senderEmails: invalid ? ts('inboundEmail.senderEmailInvalid') : undefined,
    }));
    setIsDirty(true);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('equipmentForm.editTitle') : t('equipmentForm.createTitle')}
      isDirty={isDirty}
      onSubmit={() => { void handleSubmit(); }}
      isLoading={isPending}
      submitLabel={isEditing ? tc('save') : tc('add')}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            label={t('equipmentList.name')}
            value={formData.name}
            onChange={(e) => { handleChange('name', e.target.value); }}
            helperText={errors.name ?? t('equipmentForm.nameHint')}
            error={!!errors.name}
            fullWidth
            required
          />

          <TextField
            label={t('equipmentList.identifier')}
            value={formData.identifier}
            onChange={(e) => { handleChange('identifier', e.target.value); }}
            helperText={errors.identifier ?? t('equipmentForm.identifierHint')}
            error={!!errors.identifier}
            fullWidth
            required
          />

          <TextField
            label={t('equipmentList.location')}
            value={formData.location}
            onChange={(e) => { handleChange('location', e.target.value); }}
            placeholder={t('equipmentForm.locationPlaceholder')}
            fullWidth
          />

          <TextField
            label={t('equipmentList.description')}
            value={formData.description}
            onChange={(e) => { handleChange('description', e.target.value); }}
            placeholder={t('equipmentForm.descriptionPlaceholder')}
            multiline
            rows={3}
            fullWidth
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={formData.senderEmails}
            onChange={(_event, value) => {
              handleSenderEmailsChange(value);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} label={option} size="small" {...tagProps} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={ts('inboundEmail.senderEmails')}
                helperText={errors.senderEmails ?? ts('inboundEmail.senderEmailsHint')}
                error={!!errors.senderEmails}
              />
            )}
          />
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
